import {
  type FormEvent,
  type ReactNode,
  useMemo,
  useState,
} from "react";
import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  Activity,
  AppWindow,
  ArrowUpRight,
  BarChart3,
  BriefcaseBusiness,
  Check,
  ChevronDown,
  CircleGauge,
  Copy,
  Download,
  Eye,
  EyeOff,
  FileImage,
  Gauge,
  LayoutDashboard,
  LogOut,
  Megaphone,
  Menu,
  MousePointerClick,
  PackageOpen,
  PanelTop,
  Pause,
  Play,
  Plus,
  RadioTower,
  Settings2,
  ShieldCheck,
  Target,
  Users,
  X,
} from "lucide-react";
import type {
  Advertiser,
  Campaign,
  CampaignMetrics,
  CampaignStatus,
  CreateCampaignInput,
  DashboardPayload,
  Metrics,
  Placement,
  Project,
  SessionUser,
  UserRole,
} from "@sponsor-hub/contracts";

import { api, ApiError } from "./api";

type View =
  | "dashboard"
  | "campaigns"
  | "inventory"
  | "advertisers"
  | "team"
  | "integration"
  | "account";

type UserListItem = {
  id: string;
  email: string;
  name: string;
  role: UserRole;
  advertiserId: string | null;
  advertiserName: string | null;
  active: boolean;
  createdAt: string;
};

const numberFormatter = new Intl.NumberFormat("fr-FR");
const compactFormatter = new Intl.NumberFormat("fr-FR", {
  notation: "compact",
  maximumFractionDigits: 1,
});
const dateFormatter = new Intl.DateTimeFormat("fr-FR", {
  day: "2-digit",
  month: "short",
  year: "numeric",
});

function formatNumber(value: number): string {
  return value >= 100_000
    ? compactFormatter.format(value)
    : numberFormatter.format(value);
}

function formatPercent(value: number | null): string {
  return value === null ? "—" : `${value.toLocaleString("fr-FR")} %`;
}

function dateInputValue(date: Date): string {
  const offset = date.getTimezoneOffset();
  return new Date(date.getTime() - offset * 60_000).toISOString().slice(0, 16);
}

function statusLabel(status: CampaignStatus): string {
  return {
    DRAFT: "Brouillon",
    ACTIVE: "Active",
    PAUSED: "En pause",
    ENDED: "Terminée",
  }[status];
}

function roleLabel(role: UserRole): string {
  return {
    OWNER: "Propriétaire",
    MANAGER: "Gestionnaire",
    SPONSOR_VIEWER: "Sponsor",
  }[role];
}

export function App() {
  const session = useQuery({
    queryKey: ["session"],
    queryFn: () => api.get<{ user: SessionUser }>("/api/auth/session"),
  });

  if (session.isLoading) return <Splash />;
  if (session.isError) return <Login />;
  if (!session.data) return <Splash />;
  if (session.data.user.mustChangePassword) {
    return <ForcedPassword user={session.data.user} />;
  }
  return <Workspace user={session.data.user} />;
}

function Splash() {
  return (
    <main className="centered">
      <div className="brand-mark large">
        <RadioTower size={25} />
      </div>
      <p className="muted">Ouverture de Sponsor Hub…</p>
    </main>
  );
}

function Login() {
  const queryClient = useQueryClient();
  const [showPassword, setShowPassword] = useState(false);
  const mutation = useMutation({
    mutationFn: (input: { email: string; password: string }) =>
      api.post<{ user: SessionUser }>("/api/auth/login", input),
    onSuccess: (data) => {
      queryClient.setQueryData(["session"], data);
    },
  });

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    mutation.mutate({
      email: String(data.get("email")),
      password: String(data.get("password")),
    });
  }

  return (
    <main className="login-shell">
      <section className="login-story">
        <div className="brand">
          <span className="brand-mark">
            <RadioTower size={20} />
          </span>
          <span>Sponsor Hub</span>
        </div>
        <div className="story-copy">
          <p className="eyebrow">Diffusion sponsor multi-projets</p>
          <h1>Une campagne. Des chiffres défendables.</h1>
          <p>
            Pilotez vos habillages, bannières et formats natifs avec une mesure
            séparant diffusion, impression et visibilité réelle.
          </p>
        </div>
        <div className="login-proof">
          <ShieldCheck size={18} />
          <span>Portail privé · données isolées · événements dédupliqués</span>
        </div>
      </section>
      <section className="login-panel">
        <form className="login-card" onSubmit={submit}>
          <div>
            <p className="eyebrow">Espace sécurisé</p>
            <h2>Connexion</h2>
            <p className="muted">
              Utilisez le compte administrateur ou sponsor qui vous a été
              attribué.
            </p>
          </div>
          <label>
            Adresse e-mail
            <input
              autoComplete="email"
              name="email"
              type="email"
              placeholder="vous@entreprise.dz"
              required
            />
          </label>
          <label>
            Mot de passe
            <span className="password-field">
              <input
                autoComplete="current-password"
                name="password"
                type={showPassword ? "text" : "password"}
                required
              />
              <button
                aria-label={
                  showPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"
                }
                className="icon-button password-toggle"
                onClick={() => setShowPassword((value) => !value)}
                type="button"
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </span>
          </label>
          {mutation.isError ? (
            <InlineError error={mutation.error} />
          ) : null}
          <button className="button primary wide" disabled={mutation.isPending}>
            {mutation.isPending ? "Connexion…" : "Accéder au portail"}
          </button>
        </form>
      </section>
    </main>
  );
}

function ForcedPassword({ user }: { user: SessionUser }) {
  return (
    <main className="forced-password-shell">
      <div className="brand">
        <span className="brand-mark">
          <RadioTower size={20} />
        </span>
        <span>Sponsor Hub</span>
      </div>
      <section className="panel forced-password-card">
        <span className="metric-icon violet"><ShieldCheck size={20} /></span>
        <div>
          <p className="eyebrow">Première connexion</p>
          <h1>Sécurisez votre compte</h1>
          <p className="muted">
            Bonjour {user.name}. Remplacez le mot de passe temporaire avant
            d’accéder aux statistiques.
          </p>
        </div>
        <PasswordForm />
      </section>
    </main>
  );
}

function Workspace({ user }: { user: SessionUser }) {
  const queryClient = useQueryClient();
  const [view, setView] = useState<View>("dashboard");
  const [mobileMenu, setMobileMenu] = useState(false);
  const sponsorOnly = user.role === "SPONSOR_VIEWER";

  const navigation: {
    id: View;
    label: string;
    icon: typeof LayoutDashboard;
    hidden?: boolean;
  }[] = [
    { id: "dashboard", label: "Vue d’ensemble", icon: LayoutDashboard },
    { id: "campaigns", label: "Campagnes", icon: Megaphone },
    {
      id: "inventory",
      label: "Inventaire",
      icon: PanelTop,
      hidden: sponsorOnly,
    },
    {
      id: "advertisers",
      label: "Sponsors",
      icon: BriefcaseBusiness,
      hidden: sponsorOnly,
    },
    { id: "team", label: "Accès", icon: Users, hidden: user.role !== "OWNER" },
    {
      id: "integration",
      label: "Intégration",
      icon: AppWindow,
      hidden: sponsorOnly,
    },
  ];

  const logout = useMutation({
    mutationFn: () => api.post("/api/auth/logout"),
    onSuccess: () => {
      queryClient.clear();
      window.location.reload();
    },
  });

  function navigate(next: View) {
    setView(next);
    setMobileMenu(false);
  }

  return (
    <div className="workspace">
      <aside className={mobileMenu ? "sidebar open" : "sidebar"}>
        <div className="brand sidebar-brand">
          <span className="brand-mark">
            <RadioTower size={19} />
          </span>
          <span>Sponsor Hub</span>
        </div>
        <nav>
          <p className="nav-label">Pilotage</p>
          {navigation
            .filter((item) => !item.hidden)
            .map((item) => (
              <button
                className={view === item.id ? "nav-item active" : "nav-item"}
                key={item.id}
                onClick={() => navigate(item.id)}
              >
                <item.icon size={18} />
                <span>{item.label}</span>
              </button>
            ))}
        </nav>
        <div className="sidebar-footer">
          <button className="account-chip" onClick={() => navigate("account")}>
            <span className="avatar">{user.name.slice(0, 2).toUpperCase()}</span>
            <span>
              <strong>{user.name}</strong>
              <small>{roleLabel(user.role)}</small>
            </span>
            <ChevronDown size={15} />
          </button>
          <button
            aria-label="Se déconnecter"
            className="icon-button"
            onClick={() => logout.mutate()}
          >
            <LogOut size={18} />
          </button>
        </div>
      </aside>
      {mobileMenu ? (
        <button
          aria-label="Fermer la navigation"
          className="sidebar-backdrop"
          onClick={() => setMobileMenu(false)}
        />
      ) : null}
      <main className="main">
        <header className="topbar">
          <button
            aria-label="Ouvrir la navigation"
            className="icon-button mobile-menu"
            onClick={() => setMobileMenu(true)}
          >
            <Menu size={20} />
          </button>
          <div>
            <span className="organization">{user.organizationName}</span>
            {sponsorOnly ? (
              <span className="scope-badge">Vue sponsor restreinte</span>
            ) : null}
          </div>
          <span className="live-chip">
            <span />
            Mesure active
          </span>
        </header>
        <div className="page">
          {view === "dashboard" ? <Dashboard user={user} /> : null}
          {view === "campaigns" ? <Campaigns user={user} /> : null}
          {view === "inventory" && !sponsorOnly ? <Inventory /> : null}
          {view === "advertisers" && !sponsorOnly ? <Advertisers /> : null}
          {view === "team" && user.role === "OWNER" ? <Team /> : null}
          {view === "integration" && !sponsorOnly ? <Integration /> : null}
          {view === "account" ? <Account user={user} /> : null}
        </div>
      </main>
    </div>
  );
}

function Dashboard({ user }: { user: SessionUser }) {
  const [campaignId, setCampaignId] = useState("");
  const [projectId, setProjectId] = useState("");
  const [from, setFrom] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 29);
    return date.toISOString().slice(0, 10);
  });
  const [to, setTo] = useState(() => new Date().toISOString().slice(0, 10));
  const campaigns = useQuery({
    queryKey: ["campaigns"],
    queryFn: () => api.get<Campaign[]>("/api/manage/campaigns"),
  });
  const projects = useQuery({
    queryKey: ["projects"],
    queryFn: () => api.get<Project[]>("/api/manage/projects"),
    enabled: user.role !== "SPONSOR_VIEWER",
  });
  const queryString = new URLSearchParams({
    from: new Date(`${from}T00:00:00`).toISOString(),
    to: new Date(`${to}T23:59:59`).toISOString(),
    ...(campaignId ? { campaignId } : {}),
    ...(projectId ? { projectId } : {}),
  }).toString();
  const dashboard = useQuery({
    queryKey: ["dashboard", from, to, campaignId, projectId],
    queryFn: () =>
      api.get<DashboardPayload>(`/api/manage/dashboard?${queryString}`),
  });

  if (dashboard.isLoading) return <PageLoading />;
  if (dashboard.isError) return <PageError error={dashboard.error} />;
  if (!dashboard.data) return <PageLoading />;
  const dashboardData = dashboard.data;
  const { metrics } = dashboardData;
  const selectedCampaign = dashboardData.campaigns.find(
    (campaign) => campaign.campaignId === campaignId,
  );
  return (
    <>
      <PageHeader
        eyebrow={user.role === "SPONSOR_VIEWER" ? "Rapport sponsor" : "Pilotage"}
        title="Performance de diffusion"
        description="Mesures reçues et dédupliquées, sans projection commerciale mélangée aux résultats."
        actions={
          <a
            className="button secondary"
            download
            href={`/api/manage/export.csv?${queryString}`}
          >
            <Download size={16} />
            Export CSV
          </a>
        }
      />
      <section className="filter-bar">
        <label>
          Campagne
          <select
            value={campaignId}
            onChange={(event) => setCampaignId(event.target.value)}
          >
            <option value="">Toutes les campagnes</option>
            {campaigns.data?.map((campaign) => (
              <option key={campaign.id} value={campaign.id}>
                {campaign.name}
              </option>
            ))}
          </select>
        </label>
        {user.role !== "SPONSOR_VIEWER" ? (
          <label>
            Projet
            <select
              value={projectId}
              onChange={(event) => setProjectId(event.target.value)}
            >
              <option value="">Tous les projets</option>
              {projects.data?.map((project) => (
                <option key={project.id} value={project.id}>
                  {project.name}
                </option>
              ))}
            </select>
          </label>
        ) : null}
        <label>
          Du
          <input
            type="date"
            value={from}
            onChange={(event) => setFrom(event.target.value)}
          />
        </label>
        <label>
          Au
          <input
            type="date"
            value={to}
            onChange={(event) => setTo(event.target.value)}
          />
        </label>
        <span className="filter-summary">
          {dashboardData.activeCampaigns} campagne
          {dashboardData.activeCampaigns > 1 ? "s" : ""} active
          {dashboardData.activeCampaigns > 1 ? "s" : ""}
        </span>
      </section>
      <section className="metrics-grid">
        <MetricCard
          label="Impressions"
          value={formatNumber(metrics.impressions)}
          detail={`${formatNumber(metrics.uniqueReach)} personnes uniques`}
          icon={Activity}
          tone="violet"
        />
        <MetricCard
          label="Impressions visibles"
          value={formatNumber(metrics.viewableImpressions)}
          detail={`${formatPercent(metrics.viewabilityRate)} de visibilité`}
          icon={Eye}
          tone="blue"
        />
        <MetricCard
          label="Clics"
          value={formatNumber(metrics.clicks)}
          detail={`${formatPercent(metrics.ctr)} de CTR`}
          icon={MousePointerClick}
          tone="orange"
        />
        <MetricCard
          label="Taux de remplissage"
          value={formatPercent(metrics.fillRate)}
          detail={`${formatNumber(metrics.filled)} diffusions / ${formatNumber(metrics.requests)} requêtes`}
          icon={Gauge}
          tone="green"
        />
      </section>
      {selectedCampaign && selectedCampaign.metrics.goalProgress !== null ? (
        <PacingCard campaign={selectedCampaign} />
      ) : null}
      <section className="dashboard-grid">
        <article className="panel chart-panel">
          <div className="panel-heading">
            <div>
              <p className="eyebrow">Évolution quotidienne</p>
              <h3>Impressions et clics</h3>
            </div>
            <div className="legend">
              <span><i className="legend-impressions" /> Impressions</span>
              <span><i className="legend-clicks" /> Clics</span>
            </div>
          </div>
          <BarSeries metrics={metrics} />
        </article>
        <article className="panel health-panel">
          <div className="panel-heading">
            <div>
              <p className="eyebrow">Qualité de mesure</p>
              <h3>Entonnoir de diffusion</h3>
            </div>
            <CircleGauge size={20} />
          </div>
          <Funnel metrics={metrics} />
        </article>
      </section>
      <section className="panel campaign-table-panel">
        <div className="panel-heading">
          <div>
            <p className="eyebrow">Lecture commerciale</p>
            <h3>Performance par campagne</h3>
          </div>
          <span className="muted small">
            {dashboardData.campaigns.length} campagne
            {dashboardData.campaigns.length > 1 ? "s" : ""}
          </span>
        </div>
        <CampaignMetricsTable campaigns={dashboardData.campaigns} />
      </section>
    </>
  );
}

function MetricCard({
  label,
  value,
  detail,
  icon: Icon,
  tone,
}: {
  label: string;
  value: string;
  detail: string;
  icon: typeof Activity;
  tone: string;
}) {
  return (
    <article className={`metric-card ${tone}`}>
      <span className="metric-icon"><Icon size={19} /></span>
      <div>
        <p>{label}</p>
        <strong>{value}</strong>
        <small>{detail}</small>
      </div>
    </article>
  );
}

function BarSeries({ metrics }: { metrics: Metrics }) {
  const visible = metrics.series.slice(-30);
  const maximum = Math.max(1, ...visible.map((point) => point.impressions));
  return (
    <div className="bar-chart" role="img" aria-label="Impressions quotidiennes">
      {visible.map((point, index) => (
        <div
          className="bar-column"
          key={point.date}
          title={`${point.date} : ${point.impressions} impressions, ${point.clicks} clics`}
        >
          <span
            className="bar impressions"
            style={{ height: `${Math.max(2, (point.impressions / maximum) * 100)}%` }}
          >
            <span
              className="bar clicks"
              style={{
                height: `${Math.min(100, Math.max(0, (point.clicks / Math.max(1, point.impressions)) * 100))}%`,
              }}
            />
          </span>
          {index === 0 || index === visible.length - 1 ? (
            <small>{point.date.slice(5)}</small>
          ) : null}
        </div>
      ))}
      {metrics.impressions === 0 ? (
        <div className="chart-empty">Les premières impressions apparaîtront ici.</div>
      ) : null}
    </div>
  );
}

function Funnel({ metrics }: { metrics: Metrics }) {
  const rows = [
    { label: "Requêtes d’emplacement", value: metrics.requests, ratio: 100 },
    {
      label: "Publicités sélectionnées",
      value: metrics.filled,
      ratio: metrics.fillRate,
    },
    {
      label: "Impressions rendues",
      value: metrics.impressions,
      ratio: metrics.filled ? (metrics.impressions / metrics.filled) * 100 : 0,
    },
    {
      label: "Impressions visibles",
      value: metrics.viewableImpressions,
      ratio: metrics.viewabilityRate,
    },
    {
      label: "Clics",
      value: metrics.clicks,
      ratio: metrics.ctr,
    },
  ];
  return (
    <div className="funnel">
      {rows.map((row) => (
        <div className="funnel-row" key={row.label}>
          <div>
            <span>{row.label}</span>
            <strong>{formatNumber(row.value)}</strong>
          </div>
          <span className="progress-track">
            <span style={{ width: `${Math.min(100, row.ratio)}%` }} />
          </span>
        </div>
      ))}
    </div>
  );
}

function PacingCard({ campaign }: { campaign: CampaignMetrics }) {
  const goal = campaign.metrics.goalProgress ?? 0;
  const expected = campaign.metrics.expectedProgress ?? 0;
  const pacing = campaign.metrics.pacingRate;
  const state =
    pacing === null ? "neutral" : pacing >= 90 ? "good" : pacing >= 70 ? "warn" : "bad";
  return (
    <section className={`pacing-card ${state}`}>
      <div>
        <span className="metric-icon"><Target size={19} /></span>
        <div>
          <p className="eyebrow">Rythme de campagne</p>
          <h3>{campaign.campaignName}</h3>
        </div>
      </div>
      <div className="pacing-progress">
        <div>
          <span>Livré {formatPercent(goal)}</span>
          <span>Attendu {formatPercent(expected)}</span>
        </div>
        <span className="progress-track">
          <span style={{ width: `${goal}%` }} />
          <i style={{ left: `${expected}%` }} />
        </span>
      </div>
      <strong>{pacing === null ? "Nouveau" : `${Math.round(pacing)} % du rythme`}</strong>
    </section>
  );
}

function CampaignMetricsTable({ campaigns }: { campaigns: CampaignMetrics[] }) {
  if (campaigns.length === 0) {
    return <EmptyState icon={BarChart3} title="Aucune campagne sur cette période" />;
  }
  return (
    <div className="table-scroll">
      <table>
        <thead>
          <tr>
            <th>Campagne</th>
            <th>État</th>
            <th>Impressions</th>
            <th>Visibilité</th>
            <th>Clics</th>
            <th>CTR</th>
            <th>Objectif</th>
          </tr>
        </thead>
        <tbody>
          {campaigns.map((campaign) => (
            <tr key={campaign.campaignId}>
              <td>
                <strong>{campaign.campaignName}</strong>
                <small>{campaign.advertiserName}</small>
              </td>
              <td><Status status={campaign.status} /></td>
              <td>{formatNumber(campaign.metrics.impressions)}</td>
              <td>{formatPercent(campaign.metrics.viewabilityRate)}</td>
              <td>{formatNumber(campaign.metrics.clicks)}</td>
              <td>{formatPercent(campaign.metrics.ctr)}</td>
              <td className="goal-cell">
                {campaign.impressionGoal ? (
                  <>
                    <span>{formatPercent(campaign.metrics.goalProgress)}</span>
                    <span className="mini-progress">
                      <span
                        style={{ width: `${campaign.metrics.goalProgress ?? 0}%` }}
                      />
                    </span>
                  </>
                ) : (
                  "Sans plafond"
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Campaigns({ user }: { user: SessionUser }) {
  const queryClient = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const campaigns = useQuery({
    queryKey: ["campaigns"],
    queryFn: () => api.get<Campaign[]>("/api/manage/campaigns"),
  });
  const dashboard = useQuery({
    queryKey: ["dashboard", "campaign-list"],
    queryFn: () => api.get<DashboardPayload>("/api/manage/dashboard"),
  });
  const statusMutation = useMutation({
    mutationFn: ({
      id,
      status,
    }: {
      id: string;
      status: CampaignStatus;
    }) =>
      api.patch(`/api/manage/campaigns/${id}/status`, { status }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["campaigns"] });
      void queryClient.invalidateQueries({ queryKey: ["dashboard"] });
    },
  });
  const canManage = user.role !== "SPONSOR_VIEWER";
  return (
    <>
      <PageHeader
        eyebrow="Activation et suivi"
        title="Campagnes"
        description={
          canManage
            ? "Créez une campagne, choisissez son inventaire, puis activez-la lorsque la création est validée."
            : "Consultez uniquement les campagnes et résultats associés à votre compte sponsor."
        }
        actions={
          canManage ? (
            <button className="button primary" onClick={() => setCreateOpen(true)}>
              <Plus size={16} />
              Nouvelle campagne
            </button>
          ) : null
        }
      />
      {campaigns.isLoading ? <PageLoading /> : null}
      {campaigns.isError ? <PageError error={campaigns.error} /> : null}
      {campaigns.data?.length === 0 ? (
        <EmptyState
          icon={Megaphone}
          title="Aucune campagne"
          description={
            canManage
              ? "Créez votre première campagne pour commencer la diffusion."
              : "Aucune campagne ne vous a encore été attribuée."
          }
          action={
            canManage ? (
              <button className="button primary" onClick={() => setCreateOpen(true)}>
                Créer une campagne
              </button>
            ) : null
          }
        />
      ) : (
        <section className="campaign-list">
          {campaigns.data?.map((campaign) => {
            const metrics = dashboard.data?.campaigns.find(
              (item) => item.campaignId === campaign.id,
            )?.metrics;
            return (
              <article className="campaign-card" key={campaign.id}>
                <div className="campaign-main">
                  <div className="campaign-title">
                    <span className="sponsor-logo">
                      {campaign.advertiserName.slice(0, 2).toUpperCase()}
                    </span>
                    <div>
                      <div className="title-line">
                        <h3>{campaign.name}</h3>
                        <Status status={campaign.status} />
                      </div>
                      <p>
                        {campaign.advertiserName} ·{" "}
                        {dateFormatter.format(new Date(campaign.startsAt))} —{" "}
                        {dateFormatter.format(new Date(campaign.endsAt))}
                      </p>
                    </div>
                  </div>
                  <div className="campaign-placements">
                    {campaign.placements.map((placement) => (
                      <span key={placement.id}>
                        {placement.projectName} / {placement.code}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="campaign-kpis">
                  <div>
                    <small>Impressions</small>
                    <strong>{formatNumber(metrics?.impressions ?? 0)}</strong>
                  </div>
                  <div>
                    <small>Visibilité</small>
                    <strong>{formatPercent(metrics?.viewabilityRate ?? 0)}</strong>
                  </div>
                  <div>
                    <small>CTR</small>
                    <strong>{formatPercent(metrics?.ctr ?? 0)}</strong>
                  </div>
                  <div>
                    <small>Objectif</small>
                    <strong>
                      {campaign.impressionGoal
                        ? formatPercent(metrics?.goalProgress ?? 0)
                        : "Continu"}
                    </strong>
                  </div>
                </div>
                <div className="creative-preview">
                  {campaign.creatives[0]?.assetUrl ? (
                    <img
                      alt=""
                      src={campaign.creatives[0].assetUrl}
                    />
                  ) : (
                    <span
                      className="creative-color"
                      style={{
                        background: campaign.creatives[0]?.style.background,
                        color: campaign.creatives[0]?.style.foreground,
                      }}
                    >
                      <strong>{campaign.creatives[0]?.headline}</strong>
                      <small>{campaign.creatives[0]?.ctaLabel}</small>
                    </span>
                  )}
                </div>
                {canManage ? (
                  <div className="campaign-actions">
                    {campaign.status === "ACTIVE" ? (
                      <button
                        className="button secondary compact"
                        disabled={statusMutation.isPending}
                        onClick={() =>
                          statusMutation.mutate({
                            id: campaign.id,
                            status: "PAUSED",
                          })
                        }
                      >
                        <Pause size={14} /> Mettre en pause
                      </button>
                    ) : campaign.status !== "ENDED" ? (
                      <button
                        className="button primary compact"
                        disabled={statusMutation.isPending}
                        onClick={() =>
                          statusMutation.mutate({
                            id: campaign.id,
                            status: "ACTIVE",
                          })
                        }
                      >
                        <Play size={14} /> Activer
                      </button>
                    ) : null}
                  </div>
                ) : null}
              </article>
            );
          })}
        </section>
      )}
      {createOpen ? (
        <CampaignModal onClose={() => setCreateOpen(false)} />
      ) : null}
    </>
  );
}

function CampaignModal({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const advertisers = useQuery({
    queryKey: ["advertisers"],
    queryFn: () => api.get<Advertiser[]>("/api/manage/advertisers"),
  });
  const placements = useQuery({
    queryKey: ["placements"],
    queryFn: () => api.get<Placement[]>("/api/manage/placements"),
  });
  const [placementIds, setPlacementIds] = useState<string[]>([]);
  const [format, setFormat] = useState<"NATIVE" | "BANNER" | "THEME">("NATIVE");
  const [asset, setAsset] = useState<{ id: string; url: string } | null>(null);
  const [assetPending, setAssetPending] = useState(false);
  const now = useMemo(() => new Date(), []);
  const endsAt = useMemo(() => {
    const date = new Date(now);
    date.setDate(date.getDate() + 30);
    return date;
  }, [now]);
  const mutation = useMutation({
    mutationFn: (input: CreateCampaignInput) =>
      api.post<{ id: string }>("/api/manage/campaigns", input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["campaigns"] });
      void queryClient.invalidateQueries({ queryKey: ["dashboard"] });
      onClose();
    },
  });

  async function upload(file: File) {
    setAssetPending(true);
    try {
      const body = new FormData();
      body.append("file", file);
      setAsset(
        await api.upload<{ id: string; url: string }>("/api/manage/assets", body),
      );
    } finally {
      setAssetPending(false);
    }
  }

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    mutation.mutate({
      advertiserId: String(data.get("advertiserId")),
      name: String(data.get("name")),
      startsAt: new Date(String(data.get("startsAt"))).toISOString(),
      endsAt: new Date(String(data.get("endsAt"))).toISOString(),
      impressionGoal: data.get("impressionGoal")
        ? Number(data.get("impressionGoal"))
        : null,
      dailyCap: data.get("dailyCap") ? Number(data.get("dailyCap")) : null,
      priority: Number(data.get("priority")),
      placementIds,
      creative: {
        name: String(data.get("creativeName")),
        format,
        headline: String(data.get("headline")),
        body: String(data.get("body") || "") || null,
        ctaLabel: String(data.get("ctaLabel")),
        clickUrl: String(data.get("clickUrl")),
        assetId: asset?.id ?? null,
        style: {
          background: String(data.get("background")),
          foreground: String(data.get("foreground")),
          accent: String(data.get("accent")),
          muted: String(data.get("muted")),
        },
        theme:
          format === "THEME"
            ? {
                name: String(data.get("themeName")),
                description: String(data.get("themeDescription")),
                badge: String(data.get("themeBadge")),
                palette: {
                  background: String(data.get("themeBackground")),
                  surface: String(data.get("themeSurface")),
                  elevated: String(data.get("themeElevated")),
                  text: String(data.get("themeText")),
                  muted: String(data.get("themeMuted")),
                  border: String(data.get("themeBorder")),
                  accent: String(data.get("themeAccent")),
                  accentText: String(data.get("themeAccentText")),
                  live: String(data.get("themeLive")),
                },
              }
            : null,
      },
    });
  }

  return (
    <Modal
      title="Nouvelle campagne"
      subtitle="La campagne sera créée en brouillon : aucune diffusion avant activation."
      onClose={onClose}
      wide
    >
      <form className="campaign-form" onSubmit={submit}>
        <FormSection number="1" title="Cadre commercial">
          <div className="form-grid two">
            <label>
              Sponsor
              <select name="advertiserId" required defaultValue="">
                <option value="" disabled>Choisir un sponsor</option>
                {advertisers.data?.map((advertiser) => (
                  <option key={advertiser.id} value={advertiser.id}>
                    {advertiser.name}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Nom de la campagne
              <input name="name" placeholder="Ramadan 2026 — notoriété" required />
            </label>
            <label>
              Début
              <input
                name="startsAt"
                type="datetime-local"
                defaultValue={dateInputValue(now)}
                required
              />
            </label>
            <label>
              Fin
              <input
                name="endsAt"
                type="datetime-local"
                defaultValue={dateInputValue(endsAt)}
                required
              />
            </label>
            <label>
              Objectif d’impressions
              <input
                name="impressionGoal"
                type="number"
                min="1"
                placeholder="100 000"
              />
            </label>
            <label>
              Plafond quotidien
              <input name="dailyCap" type="number" min="1" placeholder="5 000" />
            </label>
            <label>
              Priorité de diffusion
              <input
                name="priority"
                type="range"
                min="1"
                max="100"
                defaultValue="50"
              />
              <small className="field-hint">1 faible · 100 prioritaire</small>
            </label>
          </div>
        </FormSection>
        <FormSection number="2" title="Emplacements">
          <div className="placement-picker">
            {placements.data
              ?.filter((placement) => placement.kind === format)
              .map((placement) => {
              const selected = placementIds.includes(placement.id);
              return (
                <button
                  className={selected ? "placement-option selected" : "placement-option"}
                  key={placement.id}
                  onClick={() =>
                    setPlacementIds((current) =>
                      selected
                        ? current.filter((id) => id !== placement.id)
                        : [...current, placement.id],
                    )
                  }
                  type="button"
                >
                  <span className="check-box">{selected ? <Check size={14} /> : null}</span>
                  <span>
                    <strong>{placement.name}</strong>
                    <small>{placement.projectName} · {placement.code}</small>
                  </span>
                  <em>{placement.kind}</em>
                </button>
              );
            })}
            {placements.data?.filter((placement) => placement.kind === format)
              .length === 0 ? (
              <p className="muted">
                Aucun emplacement {format.toLowerCase()} : créez-en un dans
                Inventaire.
              </p>
            ) : null}
          </div>
        </FormSection>
        <FormSection number="3" title="Création publicitaire">
          <div className="form-grid two">
            <label>
              Nom interne de la création
              <input name="creativeName" placeholder="Bannière principale" required />
            </label>
            <label>
              Format
              <select
                name="format"
                value={format}
                onChange={(event) => {
                  setFormat(
                    event.target.value as "NATIVE" | "BANNER" | "THEME",
                  );
                  setPlacementIds([]);
                }}
              >
                <option value="NATIVE">Natif</option>
                <option value="BANNER">Bannière</option>
                <option value="THEME">Thème distant</option>
              </select>
            </label>
            <label className="span-two">
              Titre
              <input
                name="headline"
                placeholder="Un message clair pour votre audience"
                maxLength={180}
                required
              />
            </label>
            <label className="span-two">
              Description
              <textarea name="body" rows={3} maxLength={600} />
            </label>
            <label>
              Texte du bouton
              <input name="ctaLabel" defaultValue="Découvrir" required />
            </label>
            <label>
              Destination HTTPS
              <input
                name="clickUrl"
                type="url"
                placeholder="https://sponsor.dz/offre"
                required
              />
            </label>
          </div>
          <label className="upload-zone">
            <FileImage size={24} />
            <span>
              <strong>
                {assetPending
                  ? "Envoi de l’image…"
                  : asset
                    ? "Image chargée"
                    : "Ajouter une image"}
              </strong>
              <small>PNG, JPEG, WebP ou GIF · 3 Mo maximum</small>
            </span>
            {asset ? <Check size={20} className="success-icon" /> : null}
            <input
              accept="image/png,image/jpeg,image/webp,image/gif"
              disabled={assetPending}
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (file) void upload(file);
              }}
              type="file"
            />
          </label>
          <div className="palette-fields">
            <label>Fond <input name="background" type="color" defaultValue="#101827" /></label>
            <label>Texte <input name="foreground" type="color" defaultValue="#f8fafc" /></label>
            <label>Accent <input name="accent" type="color" defaultValue="#8b5cf6" /></label>
            <label>Secondaire <input name="muted" type="color" defaultValue="#94a3b8" /></label>
          </div>
          {format === "THEME" ? (
            <div className="theme-editor">
              <div className="form-grid two">
                <label>
                  Nom public du thème
                  <input
                    name="themeName"
                    defaultValue="Édition partenaire"
                    maxLength={120}
                    required
                  />
                </label>
                <label>
                  Badge
                  <input
                    name="themeBadge"
                    defaultValue="Édition sponsor"
                    maxLength={80}
                    required
                  />
                </label>
                <label className="span-two">
                  Description du thème
                  <input
                    name="themeDescription"
                    defaultValue="Un habillage temporaire proposé par notre partenaire."
                    maxLength={240}
                    required
                  />
                </label>
              </div>
              <p className="field-hint">
                Palette complète utilisée à distance par iOS, Android et le web.
              </p>
              <div className="palette-fields">
                <label>Arrière-plan <input name="themeBackground" type="color" defaultValue="#031911" /></label>
                <label>Surface <input name="themeSurface" type="color" defaultValue="#0d2b21" /></label>
                <label>Surface haute <input name="themeElevated" type="color" defaultValue="#153b2e" /></label>
                <label>Texte <input name="themeText" type="color" defaultValue="#f8f4e8" /></label>
                <label>Texte secondaire <input name="themeMuted" type="color" defaultValue="#9fb5aa" /></label>
                <label>Bordure <input name="themeBorder" type="color" defaultValue="#255142" /></label>
                <label>Accent <input name="themeAccent" type="color" defaultValue="#36dd8b" /></label>
                <label>Texte sur accent <input name="themeAccentText" type="color" defaultValue="#032116" /></label>
                <label>Direct / alerte <input name="themeLive" type="color" defaultValue="#ff5563" /></label>
              </div>
            </div>
          ) : null}
        </FormSection>
        {placementIds.length === 0 ? (
          <p className="form-warning">Sélectionnez au moins un emplacement.</p>
        ) : null}
        {mutation.isError ? <InlineError error={mutation.error} /> : null}
        <div className="modal-actions">
          <button className="button ghost" type="button" onClick={onClose}>
            Annuler
          </button>
          <button
            className="button primary"
            disabled={mutation.isPending || assetPending || placementIds.length === 0}
          >
            {mutation.isPending ? "Création…" : "Créer le brouillon"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function Inventory() {
  const [projectOpen, setProjectOpen] = useState(false);
  const [placementOpen, setPlacementOpen] = useState(false);
  const projects = useQuery({
    queryKey: ["projects"],
    queryFn: () => api.get<Project[]>("/api/manage/projects"),
  });
  const placements = useQuery({
    queryKey: ["placements"],
    queryFn: () => api.get<Placement[]>("/api/manage/placements"),
  });
  return (
    <>
      <PageHeader
        eyebrow="Inventaire de diffusion"
        title="Projets et emplacements"
        description="Chaque application garde sa clé publique, ses origines autorisées et ses emplacements indépendants."
        actions={
          <>
            <button className="button secondary" onClick={() => setPlacementOpen(true)}>
              <Plus size={16} /> Emplacement
            </button>
            <button className="button primary" onClick={() => setProjectOpen(true)}>
              <Plus size={16} /> Projet
            </button>
          </>
        }
      />
      <section className="inventory-grid">
        {projects.data?.map((project) => {
          const projectPlacements =
            placements.data?.filter((placement) => placement.projectId === project.id) ?? [];
          return (
            <article className="project-card" key={project.id}>
              <div className="project-heading">
                <span className="project-icon"><AppWindow size={20} /></span>
                <div>
                  <h3>{project.name}</h3>
                  <p>{project.slug}</p>
                </div>
                <span className="status active">Actif</span>
              </div>
              <div className="project-key">
                <span>
                  <small>Clé publique</small>
                  <code>{project.publicKey}</code>
                </span>
                <CopyButton value={project.publicKey} />
              </div>
              <div className="origin-list">
                <small>Origines web autorisées</small>
                {project.allowedOrigins.length ? (
                  project.allowedOrigins.map((origin) => <span key={origin}>{origin}</span>)
                ) : (
                  <span className="warning-chip">Toutes — à restreindre</span>
                )}
              </div>
              <div className="placement-list">
                <div>
                  <small>Emplacements</small>
                  <strong>{projectPlacements.length}</strong>
                </div>
                {projectPlacements.map((placement) => (
                  <span key={placement.id}>
                    <i>{placement.kind.slice(0, 1)}</i>
                    <span>
                      <strong>{placement.name}</strong>
                      <code>{placement.code}</code>
                    </span>
                  </span>
                ))}
              </div>
            </article>
          );
        })}
        {projects.data?.length === 0 ? (
          <EmptyState
            icon={PackageOpen}
            title="Aucun projet"
            description="Ajoutez DZ Score ou une autre application pour créer son inventaire."
            action={
              <button className="button primary" onClick={() => setProjectOpen(true)}>
                Ajouter un projet
              </button>
            }
          />
        ) : null}
      </section>
      {projectOpen ? <ProjectModal onClose={() => setProjectOpen(false)} /> : null}
      {placementOpen ? (
        <PlacementModal
          projects={projects.data ?? []}
          onClose={() => setPlacementOpen(false)}
        />
      ) : null}
    </>
  );
}

function ProjectModal({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (input: {
      name: string;
      slug: string;
      allowedOrigins: string[];
      allowedBundleIds: string[];
    }) => api.post<Project>("/api/manage/projects", input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["projects"] });
      onClose();
    },
  });
  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const lines = (value: FormDataEntryValue | null) =>
      String(value ?? "")
        .split(/\s+/)
        .map((item) => item.trim())
        .filter(Boolean);
    mutation.mutate({
      name: String(data.get("name")),
      slug: String(data.get("slug")),
      allowedOrigins: lines(data.get("allowedOrigins")),
      allowedBundleIds: lines(data.get("allowedBundleIds")),
    });
  }
  return (
    <Modal title="Ajouter un projet" subtitle="Une application ou un site qui consommera des campagnes." onClose={onClose}>
      <form className="stack-form" onSubmit={submit}>
        <label>Nom <input name="name" placeholder="DZ Score" required /></label>
        <label>
          Identifiant
          <input name="slug" placeholder="dz-score" pattern="[a-z0-9-]+" required />
        </label>
        <label>
          Origines web autorisées
          <textarea
            name="allowedOrigins"
            rows={3}
            placeholder="https://dz-score.example.com"
          />
          <small className="field-hint">Une URL par ligne. Laissez vide uniquement pour une intégration serveur.</small>
        </label>
        <label>
          Bundle IDs mobiles autorisés
          <textarea
            name="allowedBundleIds"
            rows={3}
            placeholder={"com.example.dzscore\ncom.example.dzscore.android"}
          />
        </label>
        {mutation.isError ? <InlineError error={mutation.error} /> : null}
        <ModalActions pending={mutation.isPending} onClose={onClose} label="Créer le projet" />
      </form>
    </Modal>
  );
}

function PlacementModal({
  projects,
  onClose,
}: {
  projects: Project[];
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (input: {
      projectId: string;
      name: string;
      code: string;
      kind: "NATIVE" | "BANNER" | "THEME";
    }) => api.post<Placement>("/api/manage/placements", input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["placements"] });
      onClose();
    },
  });
  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    mutation.mutate({
      projectId: String(data.get("projectId")),
      name: String(data.get("name")),
      code: String(data.get("code")),
      kind: String(data.get("kind")) as "NATIVE" | "BANNER" | "THEME",
    });
  }
  return (
    <Modal title="Nouvel emplacement" subtitle="Une surface publicitaire stable dans votre produit." onClose={onClose}>
      <form className="stack-form" onSubmit={submit}>
        <label>
          Projet
          <select name="projectId" required defaultValue="">
            <option value="" disabled>Choisir un projet</option>
            {projects.map((project) => (
              <option key={project.id} value={project.id}>{project.name}</option>
            ))}
          </select>
        </label>
        <label>Nom <input name="name" placeholder="Accueil — après les scores" required /></label>
        <label>
          Code technique
          <input name="code" placeholder="home_after_scores" pattern="[a-z0-9_]+" required />
        </label>
        <label>
          Type
          <select name="kind">
            <option value="NATIVE">Format natif</option>
            <option value="BANNER">Bannière</option>
            <option value="THEME">Thème distant</option>
          </select>
        </label>
        {mutation.isError ? <InlineError error={mutation.error} /> : null}
        <ModalActions pending={mutation.isPending} onClose={onClose} label="Créer l’emplacement" />
      </form>
    </Modal>
  );
}

function Advertisers() {
  const [open, setOpen] = useState(false);
  const advertisers = useQuery({
    queryKey: ["advertisers"],
    queryFn: () => api.get<Advertiser[]>("/api/manage/advertisers"),
  });
  const campaigns = useQuery({
    queryKey: ["campaigns"],
    queryFn: () => api.get<Campaign[]>("/api/manage/campaigns"),
  });
  return (
    <>
      <PageHeader
        eyebrow="Portefeuille commercial"
        title="Sponsors"
        description="Centralisez les annonceurs et rattachez leurs accès aux seules campagnes qui les concernent."
        actions={
          <button className="button primary" onClick={() => setOpen(true)}>
            <Plus size={16} /> Ajouter un sponsor
          </button>
        }
      />
      <section className="advertiser-grid">
        {advertisers.data?.map((advertiser) => {
          const related =
            campaigns.data?.filter((campaign) => campaign.advertiserId === advertiser.id) ?? [];
          const active = related.filter((campaign) => campaign.status === "ACTIVE").length;
          return (
            <article className="advertiser-card" key={advertiser.id}>
              <span className="advertiser-logo">
                {advertiser.name.slice(0, 2).toUpperCase()}
              </span>
              <div>
                <h3>{advertiser.name}</h3>
                <p>{advertiser.contactName || "Contact non renseigné"}</p>
                <small>{advertiser.contactEmail || "E-mail non renseigné"}</small>
              </div>
              <div className="advertiser-stats">
                <span><strong>{related.length}</strong> campagnes</span>
                <span><strong>{active}</strong> actives</span>
              </div>
            </article>
          );
        })}
      </section>
      {advertisers.data?.length === 0 ? (
        <EmptyState
          icon={BriefcaseBusiness}
          title="Aucun sponsor"
          description="Ajoutez une marque avant de créer sa première campagne."
        />
      ) : null}
      {open ? <AdvertiserModal onClose={() => setOpen(false)} /> : null}
    </>
  );
}

function AdvertiserModal({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (input: {
      name: string;
      contactName: string | null;
      contactEmail: string | null;
    }) => api.post<Advertiser>("/api/manage/advertisers", input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["advertisers"] });
      onClose();
    },
  });
  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    mutation.mutate({
      name: String(data.get("name")),
      contactName: String(data.get("contactName") || "") || null,
      contactEmail: String(data.get("contactEmail") || "") || null,
    });
  }
  return (
    <Modal title="Ajouter un sponsor" subtitle="L’annonceur pourra ensuite recevoir un accès en lecture seule." onClose={onClose}>
      <form className="stack-form" onSubmit={submit}>
        <label>Nom de la marque <input name="name" placeholder="Entreprise DZ" required /></label>
        <label>Contact <input name="contactName" placeholder="Nom et prénom" /></label>
        <label>E-mail <input name="contactEmail" type="email" placeholder="contact@entreprise.dz" /></label>
        {mutation.isError ? <InlineError error={mutation.error} /> : null}
        <ModalActions pending={mutation.isPending} onClose={onClose} label="Ajouter le sponsor" />
      </form>
    </Modal>
  );
}

function Team() {
  const [open, setOpen] = useState(false);
  const users = useQuery({
    queryKey: ["users"],
    queryFn: () => api.get<UserListItem[]>("/api/manage/users"),
  });
  return (
    <>
      <PageHeader
        eyebrow="Contrôle des accès"
        title="Équipe et comptes sponsor"
        description="Les sponsors ne voient que leurs campagnes. Les gestionnaires peuvent administrer l’inventaire et la diffusion."
        actions={
          <button className="button primary" onClick={() => setOpen(true)}>
            <Plus size={16} /> Inviter un utilisateur
          </button>
        }
      />
      <section className="panel">
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Utilisateur</th>
                <th>Rôle</th>
                <th>Périmètre</th>
                <th>État</th>
                <th>Créé le</th>
              </tr>
            </thead>
            <tbody>
              {users.data?.map((user) => (
                <tr key={user.id}>
                  <td>
                    <strong>{user.name}</strong>
                    <small>{user.email}</small>
                  </td>
                  <td>{roleLabel(user.role)}</td>
                  <td>{user.advertiserName ?? "Toute l’organisation"}</td>
                  <td><span className="status active">Actif</span></td>
                  <td>{dateFormatter.format(new Date(user.createdAt))}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
      {open ? <UserModal onClose={() => setOpen(false)} /> : null}
    </>
  );
}

function UserModal({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const [role, setRole] = useState<UserRole>("SPONSOR_VIEWER");
  const advertisers = useQuery({
    queryKey: ["advertisers"],
    queryFn: () => api.get<Advertiser[]>("/api/manage/advertisers"),
  });
  const mutation = useMutation({
    mutationFn: (input: {
      name: string;
      email: string;
      password: string;
      role: UserRole;
      advertiserId: string | null;
    }) => api.post<{ id: string }>("/api/manage/users", input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["users"] });
      onClose();
    },
  });
  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    mutation.mutate({
      name: String(data.get("name")),
      email: String(data.get("email")),
      password: String(data.get("password")),
      role,
      advertiserId:
        role === "SPONSOR_VIEWER" ? String(data.get("advertiserId")) : null,
    });
  }
  return (
    <Modal title="Créer un accès" subtitle="Le mot de passe doit être transmis au destinataire par un canal sûr." onClose={onClose}>
      <form className="stack-form" onSubmit={submit}>
        <label>Nom <input name="name" required /></label>
        <label>E-mail <input name="email" type="email" required /></label>
        <label>
          Rôle
          <select value={role} onChange={(event) => setRole(event.target.value as UserRole)}>
            <option value="SPONSOR_VIEWER">Sponsor — lecture restreinte</option>
            <option value="MANAGER">Gestionnaire</option>
            <option value="OWNER">Propriétaire</option>
          </select>
        </label>
        {role === "SPONSOR_VIEWER" ? (
          <label>
            Sponsor associé
            <select name="advertiserId" required defaultValue="">
              <option value="" disabled>Choisir un sponsor</option>
              {advertisers.data?.map((advertiser) => (
                <option value={advertiser.id} key={advertiser.id}>{advertiser.name}</option>
              ))}
            </select>
          </label>
        ) : null}
        <label>
          Mot de passe temporaire
          <input
            name="password"
            type="password"
            minLength={12}
            pattern="(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9]).{12,}"
            required
          />
          <small className="field-hint">12 caractères, majuscule, minuscule et chiffre.</small>
        </label>
        {mutation.isError ? <InlineError error={mutation.error} /> : null}
        <ModalActions pending={mutation.isPending} onClose={onClose} label="Créer l’accès" />
      </form>
    </Modal>
  );
}

function Integration() {
  const projects = useQuery({
    queryKey: ["projects"],
    queryFn: () => api.get<Project[]>("/api/manage/projects"),
  });
  const placements = useQuery({
    queryKey: ["placements"],
    queryFn: () => api.get<Placement[]>("/api/manage/placements"),
  });
  const [projectId, setProjectId] = useState("");
  const selectedProject =
    projects.data?.find((project) => project.id === projectId) ?? projects.data?.[0];
  const firstPlacement = placements.data?.find(
    (placement) => placement.projectId === selectedProject?.id,
  );
  const snippet = selectedProject && firstPlacement
    ? `import { SponsorHubClient } from "@sponsor-hub/sdk";

const sponsorHub = new SponsorHubClient({
  baseUrl: "${window.location.origin}",
  projectKey: "${selectedProject.publicKey}",
  anonymousId: () => getAnonymousId(),
  platform: "web"
});

const ad = await sponsorHub.requestAd({
  placement: "${firstPlacement.code}"
});

if (ad.filled) {
  renderCreative(ad.creative);
  await sponsorHub.trackRender(ad.trackingToken);
}`
    : "Créez un projet et un emplacement pour obtenir le code d’intégration.";
  return (
    <>
      <PageHeader
        eyebrow="SDK universel"
        title="Intégration"
        description="Le même protocole fonctionne sur le web, React Native, Expo et vos futurs projets."
      />
      <section className="integration-layout">
        <article className="panel integration-config">
          <h3>Configuration du projet</h3>
          <label>
            Projet
            <select
              value={selectedProject?.id ?? ""}
              onChange={(event) => setProjectId(event.target.value)}
            >
              {projects.data?.map((project) => (
                <option key={project.id} value={project.id}>{project.name}</option>
              ))}
            </select>
          </label>
          {selectedProject ? (
            <>
              <InfoRow label="Clé publique" value={selectedProject.publicKey} copy />
              <InfoRow
                label="Origines autorisées"
                value={selectedProject.allowedOrigins.join(", ") || "Aucune restriction"}
              />
              <InfoRow
                label="Emplacements"
                value={String(
                  placements.data?.filter(
                    (placement) => placement.projectId === selectedProject.id,
                  ).length ?? 0,
                )}
              />
            </>
          ) : null}
          <div className="security-note">
            <ShieldCheck size={18} />
            <p>
              La clé projet est publique. Les identifiants de campagne et les
              événements restent protégés par un jeton de diffusion signé et
              limité à 24 heures.
            </p>
          </div>
        </article>
        <article className="panel code-panel">
          <div className="panel-heading">
            <div>
              <p className="eyebrow">TypeScript</p>
              <h3>Demander et mesurer une publicité</h3>
            </div>
            <CopyButton value={snippet} label="Copier" />
          </div>
          <pre><code>{snippet}</code></pre>
          <div className="integration-steps">
            <span><i>1</i> Décision</span>
            <ArrowUpRight size={14} />
            <span><i>2</i> Rendu</span>
            <ArrowUpRight size={14} />
            <span><i>3</i> Visible 1 s</span>
            <ArrowUpRight size={14} />
            <span><i>4</i> Clic signé</span>
          </div>
        </article>
      </section>
      <section className="panel definitions">
        <div>
          <h3>Définitions de référence</h3>
          <p>Ces règles sont identiques pour tous les projets et exports.</p>
        </div>
        <dl>
          <div><dt>Diffusion</dt><dd>Une création éligible a été sélectionnée.</dd></div>
          <div><dt>Impression</dt><dd>Le client confirme que la création a été rendue.</dd></div>
          <div><dt>Impression visible</dt><dd>Au moins 50 % pendant au moins 1 seconde.</dd></div>
          <div><dt>CTR</dt><dd>Clics uniques de diffusion ÷ impressions rendues.</dd></div>
          <div><dt>Remplissage</dt><dd>Diffusions avec création ÷ requêtes d’emplacement.</dd></div>
        </dl>
      </section>
    </>
  );
}

function Account({ user }: { user: SessionUser }) {
  return (
    <>
      <PageHeader
        eyebrow="Compte"
        title={user.name}
        description={`${user.email} · ${roleLabel(user.role)}`}
      />
      <section className="panel account-panel">
        <div>
          <span className="metric-icon violet"><Settings2 size={20} /></span>
          <h3>Changer le mot de passe</h3>
          <p className="muted">
            Toutes vos sessions seront fermées après la modification.
          </p>
        </div>
        <PasswordForm />
      </section>
    </>
  );
}

function PasswordForm() {
  const queryClient = useQueryClient();
  const [done, setDone] = useState(false);
  const mutation = useMutation({
    mutationFn: (input: { currentPassword: string; nextPassword: string }) =>
      api.post("/api/auth/password", input),
    onSuccess: () => {
      setDone(true);
      queryClient.clear();
      setTimeout(() => window.location.reload(), 1_200);
    },
  });
  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    mutation.mutate({
      currentPassword: String(data.get("currentPassword")),
      nextPassword: String(data.get("nextPassword")),
    });
  }
  return (
    <form className="stack-form" onSubmit={submit}>
      <label>Mot de passe actuel <input name="currentPassword" type="password" required /></label>
      <label>
        Nouveau mot de passe
        <input
          name="nextPassword"
          type="password"
          minLength={12}
          pattern="(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9]).{12,}"
          required
        />
      </label>
      {mutation.isError ? <InlineError error={mutation.error} /> : null}
      {done ? <p className="success-message"><Check size={16} /> Mot de passe modifié</p> : null}
      <button className="button primary" disabled={mutation.isPending}>
        Mettre à jour
      </button>
    </form>
  );
}

function PageHeader({
  eyebrow,
  title,
  description,
  actions,
}: {
  eyebrow: string;
  title: string;
  description: string;
  actions?: ReactNode;
}) {
  return (
    <header className="page-header">
      <div>
        <p className="eyebrow">{eyebrow}</p>
        <h1>{title}</h1>
        <p>{description}</p>
      </div>
      {actions ? <div className="page-actions">{actions}</div> : null}
    </header>
  );
}

function Modal({
  title,
  subtitle,
  onClose,
  children,
  wide = false,
}: {
  title: string;
  subtitle: string;
  onClose: () => void;
  children: ReactNode;
  wide?: boolean;
}) {
  return (
    <div className="modal-layer" role="presentation" onMouseDown={onClose}>
      <section
        aria-labelledby="modal-title"
        aria-modal="true"
        className={wide ? "modal wide" : "modal"}
        onMouseDown={(event) => event.stopPropagation()}
        role="dialog"
      >
        <header>
          <div>
            <h2 id="modal-title">{title}</h2>
            <p>{subtitle}</p>
          </div>
          <button aria-label="Fermer" className="icon-button" onClick={onClose}>
            <X size={20} />
          </button>
        </header>
        <div className="modal-body">{children}</div>
      </section>
    </div>
  );
}

function FormSection({
  number,
  title,
  children,
}: {
  number: string;
  title: string;
  children: ReactNode;
}) {
  return (
    <section className="form-section">
      <header><span>{number}</span><h3>{title}</h3></header>
      <div>{children}</div>
    </section>
  );
}

function ModalActions({
  pending,
  onClose,
  label,
}: {
  pending: boolean;
  onClose: () => void;
  label: string;
}) {
  return (
    <div className="modal-actions">
      <button className="button ghost" onClick={onClose} type="button">Annuler</button>
      <button className="button primary" disabled={pending}>
        {pending ? "Enregistrement…" : label}
      </button>
    </div>
  );
}

function Status({ status }: { status: CampaignStatus }) {
  return <span className={`status ${status.toLowerCase()}`}>{statusLabel(status)}</span>;
}

function EmptyState({
  icon: Icon,
  title,
  description,
  action,
}: {
  icon: typeof PackageOpen;
  title: string;
  description?: string;
  action?: ReactNode;
}) {
  return (
    <section className="empty-state">
      <span><Icon size={25} /></span>
      <h3>{title}</h3>
      {description ? <p>{description}</p> : null}
      {action}
    </section>
  );
}

function CopyButton({ value, label }: { value: string; label?: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      className={label ? "button secondary compact" : "icon-button"}
      onClick={() => {
        void navigator.clipboard.writeText(value);
        setCopied(true);
        setTimeout(() => setCopied(false), 1_500);
      }}
      type="button"
    >
      {copied ? <Check size={15} /> : <Copy size={15} />}
      {label ? (copied ? "Copié" : label) : null}
    </button>
  );
}

function InfoRow({
  label,
  value,
  copy = false,
}: {
  label: string;
  value: string;
  copy?: boolean;
}) {
  return (
    <div className="info-row">
      <span><small>{label}</small><code>{value}</code></span>
      {copy ? <CopyButton value={value} /> : null}
    </div>
  );
}

function InlineError({ error }: { error: Error }) {
  return (
    <p className="error-message">
      {error instanceof ApiError ? error.message : "Une erreur est survenue."}
    </p>
  );
}

function PageError({ error }: { error: Error }) {
  return (
    <section className="page-error">
      <ShieldCheck size={24} />
      <div>
        <h3>Impossible de charger les données</h3>
        <InlineError error={error} />
      </div>
    </section>
  );
}

function PageLoading() {
  return (
    <section className="loading-grid">
      <span /><span /><span /><span />
    </section>
  );
}
